import { Injectable, inject } from '@angular/core';
import { minusDays, monthsForDayRange } from '../measurement/window-mechanics';
import type { ProductFetchRequest } from '../observation/observation-acquisition.service';
import {
  ObservationRefreshService,
  type RefreshProgress,
} from '../observation/observation-refresh.service';
import type { CacheBatchResult } from '../observation/observation-repository';
import type { EAN } from '../shared/ean';
import { LEAD_IN_DAYS } from './constants';
import { type BrandLedger, buildLedger, isIsoDay } from './ledger';

type LedgerRequest = {
  readonly brand: string;
  /** The brand's products, with the page the scraper needs when a row is stale. */
  readonly products: readonly ProductFetchRequest[];
  /** First visible day, YYYY-MM-DD. */
  readonly dateStart: string;
  /** YYYY-MM-DD. */
  readonly today: string;
  readonly signal: AbortSignal;
  readonly onProgress?: (progress: RefreshProgress) => void | Promise<void>;
};

type LedgerResult = {
  readonly ledger: BrandLedger;
  /** Products enqueued for a scrape and still stale at the deadline; their rows are in the ledger as held. */
  readonly pending: readonly EAN[];
  /** Stale products without a page, so never enqueued. */
  readonly unscrapable: readonly EAN[];
};

type Held = { readonly fingerprint: string; readonly ledger: BrandLedger; readonly days: number };

// About 150 bytes a Day; the observation cache itself holds at most 500 000 observations.
const MAX_HELD_DAYS = 1_000_000;

function dayCount(ledger: BrandLedger): number {
  let count = 0;
  for (const byEan of ledger.series.values())
    for (const days of byEan.values()) count += days.length;
  return count;
}

// The rows' `upd` per EAN is the observation cache's generation for the brand: equal rows build an equal ledger.
function fingerprintOf(
  rows: CacheBatchResult,
  eans: readonly EAN[],
  dateStart: string,
  today: string
): string {
  const perEan = [...eans].sort().map((ean) => `${ean}=${rows.get(ean)?.upd ?? '-'}`);
  return `${dateStart}|${today}|${perEan.join(',')}`;
}

/**
 * One brand's campaign ledger, computed once from the observation refresh and reused by every
 * reader while the brand's rows, EAN set and dates are unchanged (CONTEXT.md, Campaign ledger).
 *
 * The held ledgers live as long as the injector holding `portalDomainProviders`: the root
 * injector today, the lazy portal route once the portal boundary moves them there, after which
 * they die on leaving the portal. The service keeps no module-level state (ADR 0002).
 */
@Injectable()
export class CampaignLedgerService {
  private readonly refresh = inject(ObservationRefreshService);
  private readonly held = new Map<string, Held>();

  /**
   * Reads the visible range plus the lead-in, month-rounded, through the observation refresh and
   * builds the brand's ledger from every row returned. Rejects with `signal.reason` on abort.
   */
  async forBrand(request: LedgerRequest): Promise<LedgerResult> {
    const { brand, products, dateStart, today, signal, onProgress } = request;
    const empty = (): LedgerResult => ({
      ledger: buildLedger({ skus: [], dateStart, today }),
      pending: [],
      unscrapable: [],
    });
    if (!isIsoDay(dateStart) || !isIsoDay(today)) return empty();
    const axisStart = minusDays(dateStart, LEAD_IN_DAYS);
    if (axisStart > today) return empty();

    const { rows, pending, unscrapable } = await this.refresh.refresh(products, {
      signal,
      monthsYM: monthsForDayRange(axisStart, today),
      onProgress,
    });
    const eans = [...new Set(products.map(({ ean }) => ean))];
    const fingerprint = fingerprintOf(rows, eans, dateStart, today);
    const held = this.held.get(brand);
    if (held?.fingerprint === fingerprint) {
      // Re-insertion keeps the map in recency order for eviction.
      this.held.delete(brand);
      this.held.set(brand, held);
      return { ledger: held.ledger, pending, unscrapable };
    }

    const skus = eans
      .map((ean) => ({ ean, observations: rows.get(ean)?.observations ?? [] }))
      .filter((sku) => sku.observations.length > 0);
    const ledger = buildLedger({ skus, dateStart, today });
    this.held.delete(brand);
    this.held.set(brand, { fingerprint, ledger, days: dayCount(ledger) });
    this.evict();
    return { ledger, pending, unscrapable };
  }

  // Drops the least recently used brands until the held days fit, keeping the newest.
  private evict(): void {
    let total = 0;
    for (const { days } of this.held.values()) total += days;
    for (const [brand, { days }] of this.held) {
      if (total <= MAX_HELD_DAYS || this.held.size === 1) return;
      this.held.delete(brand);
      total -= days;
    }
  }
}
